(() => {
  const MAX_FILE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_MIME = ["image/jpeg", "image/png", "application/pdf"];
  const RELOAD_DELAY_MS = 1500;
  const GATE_POLL_INTERVAL_MS = 2000;
  const GATE_POLL_MAX_ATTEMPTS = 15;
  const GATE_BANNER_ID = "DocumentUploadGateBanner";
  const PENDING_CHECKOUT_KEY = "sm_id_checkout_pending";
  const PENDING_CHECKOUT_TTL_MS = 15 * 60 * 1000;
  const DEV_PREVIEW_THEME_ID = "155389264007";
  const ERROR_NOTIFY_SESSION_KEY = "sm_upload_error_notified";
  const ERROR_NOTIFY_COOLDOWN_MS = 5 * 60 * 1000;

  const config = window.documentUpload || {};
  let activeModal = null;
  let teardownFocusTrap = null;
  let scrollLockPosition = 0;
  let reloadTimer = null;
  let isSubmitting = false;
  let pendingB2cReload = false;

  function $(selector, root = document) {
    return root.querySelector(selector);
  }

  function $all(selector, root = document) {
    return Array.from(root.querySelectorAll(selector));
  }

  function lockScroll() {
    scrollLockPosition = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollLockPosition}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.documentElement.classList.add("document-modal--locked");
    document.body.classList.add("document-modal--locked");
  }

  function unlockScroll() {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    document.documentElement.classList.remove("document-modal--locked");
    document.body.classList.remove("document-modal--locked");
    window.scrollTo(0, scrollLockPosition);
  }

  function trapFocus(modalEl) {
    const selectors = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",");

    function getFocusable() {
      return Array.from(modalEl.querySelectorAll(selectors)).filter((el) => el.offsetParent !== null);
    }

    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeModal(modalEl);
        return;
      }

      if (e.key !== "Tab") return;

      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !modalEl.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }

  function resetModal(modalEl) {
    if (reloadTimer) {
      window.clearTimeout(reloadTimer);
      reloadTimer = null;
    }
    pendingB2cReload = false;

    const formView = $("[data-document-modal-form-view]", modalEl);
    const successView = $("[data-document-modal-success-view]", modalEl);
    const form = $("form[data-document-form]", modalEl);
    const errorEl = $("[data-document-form-error]", modalEl);

    if (formView) formView.hidden = false;
    if (successView) successView.hidden = true;
    if (form) form.reset();
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = "";
    }

    $all("[data-document-modal-close]", modalEl).forEach((el) => {
      el.hidden = false;
    });

    updateIdBackField(modalEl);
  }

  function isModalLocked(modalEl) {
    return isSubmitting || (pendingB2cReload && modalEl === activeModal);
  }

  function warnBeforeUnload(event) {
    if (!isSubmitting) return;
    event.preventDefault();
    event.returnValue = "";
  }

  function sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function getCustomerId() {
    return String(
      config.customerId || document.body.getAttribute("data-customer-id") || ""
    ).trim();
  }

  function setPendingCheckoutUnlock(customerId) {
    const id = String(customerId || getCustomerId() || "pending").trim();
    try {
      sessionStorage.setItem(
        PENDING_CHECKOUT_KEY,
        JSON.stringify({
          customerId: id,
          exp: Date.now() + PENDING_CHECKOUT_TTL_MS
        })
      );
    } catch (_) {
      /* ignore */
    }
  }

  function hasGateRedirectParam() {
    return new URLSearchParams(window.location.search).has("_gate");
  }

  function shouldBridgeCheckout() {
    if (isPendingCheckoutActive()) return true;
    if (hasGateRedirectParam()) {
      setPendingCheckoutUnlock(getCustomerId());
      return true;
    }
    return false;
  }

  function getPendingCheckoutUnlock() {
    try {
      const raw = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.exp || data.exp < Date.now()) {
        sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
        return null;
      }
      return data;
    } catch (_) {
      return null;
    }
  }

  function clearPendingCheckoutUnlock() {
    try {
      sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    } catch (_) {
      /* ignore */
    }
  }

  function isPendingCheckoutActive() {
    const pending = getPendingCheckoutUnlock();
    const customerId = getCustomerId();
    if (!pending) return false;
    if (!customerId) return true;
    if (String(pending.customerId) === "pending") return true;
    return String(pending.customerId) === String(customerId);
  }

  function isServerCheckoutReady() {
    return Boolean(
      document.querySelector('.cart-checkout-gate[data-can-checkout="true"]:not([data-checkout-unlocked="true"])')
    );
  }

  function maybeClearPendingIfServerReady() {
    if (isServerCheckoutReady()) {
      clearPendingCheckoutUnlock();
      $all('.cart-checkout-gate[data-checkout-unlocked="true"]').forEach((gate) => {
        gate.removeAttribute("data-checkout-unlocked");
      });
    }
  }

  function unlockCheckoutGateInDom(gate) {
    if (!gate || gate.getAttribute("data-can-checkout") === "true") {
      return false;
    }
    if (!shouldBridgeCheckout()) {
      return false;
    }
    if (gate.getAttribute("data-checkout-unlocked") === "true") {
      return true;
    }

    const cartUrl = (window.routes && window.routes.cartUrl) || "/cart";
    const isMiniCart = gate.classList.contains("cart-checkout-gate--mini-cart");

    if (isMiniCart) {
      gate.innerHTML =
        '<div class="button-group button-group--loose button-group--fit cart-checkout-gate__actions">' +
        `<a href="${cartUrl}" class="button button--secondary cart-checkout-gate__view-cart" data-no-instant>カートを見る</a>` +
        '<button type="submit" name="checkout" class="button button--primary">チェックアウト</button>' +
        "</div>";
    } else {
      gate.innerHTML =
        '<button type="submit" name="checkout" class="cart-recap__checkout button button--primary button--full button--large">チェックアウト</button>';
    }

    gate.setAttribute("data-checkout-unlocked", "true");
    gate.setAttribute("data-can-checkout", "true");
    return true;
  }

  function applyPendingCheckoutGates() {
    if (!shouldBridgeCheckout()) return;
    $all(".cart-checkout-gate").forEach(unlockCheckoutGateInDom);
    maybeClearPendingIfServerReady();
  }

  function appendPreviewThemeParam(url) {
    const current = new URL(window.location.href);
    if (current.searchParams.has("preview_theme_id")) {
      url.searchParams.set("preview_theme_id", current.searchParams.get("preview_theme_id"));
    }
  }

  function buildCartUrlWithGate() {
    const cartPath = (window.routes && window.routes.cartUrl) || "/cart";
    const url = cartPath.startsWith("http")
      ? new URL(cartPath)
      : new URL(cartPath, window.location.origin);
    appendPreviewThemeParam(url);
    url.searchParams.set("_gate", String(Date.now()));
    return url.toString();
  }

  function isCartPage() {
    if (window.theme && window.theme.pageType === "cart") {
      return true;
    }
    return Boolean(document.querySelector('[data-section-type="cart"]'));
  }

  function getCartSectionFetchUrl() {
    const cartPath = (window.routes && window.routes.cartUrl) || "/cart";
    const base = cartPath.startsWith("http")
      ? new URL(cartPath)
      : new URL(cartPath, window.location.origin);
    appendPreviewThemeParam(base);
    const isCartPage = window.theme && window.theme.pageType === "cart";

    if (isCartPage) {
      const cartSection = document.querySelector('[data-section-type="cart"]');
      const sectionId = cartSection && cartSection.getAttribute("data-section-id");
      if (sectionId) {
        base.searchParams.set("section_id", sectionId);
        base.searchParams.set("_", String(Date.now()));
        return base.toString();
      }
    }

    base.searchParams.set("section_id", "mini-cart");
    base.searchParams.set("_", String(Date.now()));
    return base.toString();
  }

  function explainBridgeFailure() {
    if (!hasGateRedirectParam()) return;

    if (!getCustomerId()) {
      showGateSyncBanner("チェックアウトを有効にできません。ストアに顧客としてログインしてください。");
      return;
    }

    if (!$(".cart-checkout-gate")) {
      showGateSyncBanner(
        "この URL は公開テーマを表示しています。Development テーマでプレビュー URL（preview_theme_id=" +
          DEV_PREVIEW_THEME_ID +
          "）からテストしてください。"
      );
    }
  }

  function reloadPageForCheckout() {
    pendingB2cReload = false;
    if (reloadTimer) {
      window.clearTimeout(reloadTimer);
      reloadTimer = null;
    }
    if (!isCartPage()) {
      window.location.assign(buildCartUrlWithGate());
      return;
    }
    syncCheckoutGateAfterTagApplied(false);
  }

  async function fetchCheckoutReadyFromSection() {
    const url = getCartSectionFetchUrl();
    const response = await fetch(url, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" }
    });

    if (!response.ok) {
      return false;
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const gate = doc.querySelector(".cart-checkout-gate");
    return gate?.getAttribute("data-can-checkout") === "true";
  }

  function showGateSyncBanner(message) {
    let banner = document.getElementById(GATE_BANNER_ID);
    if (!banner) {
      banner = document.createElement("div");
      banner.id = GATE_BANNER_ID;
      banner.className = "document-gate-banner";
      banner.setAttribute("role", "status");
      banner.setAttribute("aria-live", "polite");
      document.body.appendChild(banner);
    }
    banner.textContent = message;
    banner.hidden = false;
  }

  function hideGateSyncBanner() {
    const banner = document.getElementById(GATE_BANNER_ID);
    if (banner) {
      banner.hidden = true;
    }
  }

  function refreshCartSections() {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { scrollToTop: false }
      })
    );
  }

  function stripGateQueryParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("_gate")) {
      return;
    }
    url.searchParams.delete("_gate");
    window.history.replaceState(null, "", url.toString());
  }

  async function syncCheckoutGateAfterTagApplied(fromRedirect = false) {
    applyPendingCheckoutGates();
    refreshCartSections();

    if (shouldBridgeCheckout()) {
      showGateSyncBanner("チェックアウトを有効にしました。Shopify への反映を確認しています…");
    } else {
      showGateSyncBanner("チェックアウトを反映しています。しばらくお待ちください…");
    }

    for (let attempt = 0; attempt < GATE_POLL_MAX_ATTEMPTS; attempt += 1) {
      applyPendingCheckoutGates();

      try {
        if (await fetchCheckoutReadyFromSection()) {
          clearPendingCheckoutUnlock();
          refreshCartSections();
          await sleep(400);
          hideGateSyncBanner();
          if (fromRedirect) {
            stripGateQueryParam();
          }
          return;
        }
      } catch (error) {
        console.warn("[document-upload] gate poll failed", error);
      }

      await sleep(GATE_POLL_INTERVAL_MS);
    }

    applyPendingCheckoutGates();
    showGateSyncBanner(
      "チェックアウトは利用できます。表示が更新されない場合はカートを開き直してください。"
    );
    if (fromRedirect) {
      stripGateQueryParam();
    }
  }

  function maybeStartGateSync() {
    applyPendingCheckoutGates();
    explainBridgeFailure();
    if (!hasGateRedirectParam()) {
      return;
    }
    syncCheckoutGateAfterTagApplied(true);
  }

  function initCheckoutGateBridge() {
    applyPendingCheckoutGates();

    document.addEventListener("cart:rerendered", () => {
      applyPendingCheckoutGates();
    });

    document.documentElement.addEventListener("cart:refresh", () => {
      window.setTimeout(applyPendingCheckoutGates, 400);
    });

    if (window.theme && window.theme.pageType === "cart") {
      [300, 1000, 2500].forEach((ms) => {
        window.setTimeout(applyPendingCheckoutGates, ms);
      });
    }
  }

  function openModal(modalEl) {
    if (!modalEl) return;
    resetModal(modalEl);
    lockScroll();
    modalEl.hidden = false;
    activeModal = modalEl;
    if (teardownFocusTrap) teardownFocusTrap();
    teardownFocusTrap = trapFocus(modalEl);

    const firstInput = $(".DocModal__close, input, select, textarea, button", modalEl);
    if (firstInput) {
      try {
        firstInput.focus({ preventScroll: true });
      } catch (_) {
        firstInput.focus();
      }
    }
  }

  function closeModal(modalEl) {
    if (!modalEl) return;
    if (isModalLocked(modalEl)) return;
    modalEl.hidden = true;
    if (activeModal === modalEl) activeModal = null;
    unlockScroll();
    if (teardownFocusTrap) {
      teardownFocusTrap();
      teardownFocusTrap = null;
    }
  }

  function updateIdBackField(modalEl) {
    const select = $("[data-id-type-select]", modalEl);
    const backField = $("[data-id-back-field]", modalEl);
    const backInput = $("#id-verification-back", modalEl);
    if (!select || !backField || !backInput) return;

    const needsBack = select.value === "drivers_license" || select.value === "residence_card";
    backField.hidden = !needsBack;
    backInput.required = needsBack;
    if (!needsBack) backInput.value = "";
  }

  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const commaIndex = result.indexOf(",");
        resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(new Error("ファイルの読み込みに失敗しました。"));
      reader.readAsDataURL(file);
    });
  }

  async function collectFiles(form) {
    const files = [];
    const inputs = $all("input[type='file']", form);

    for (const input of inputs) {
      if (input.hidden || input.closest("[hidden]")) continue;
      if (!input.files || !input.files[0]) {
        if (input.required) {
          throw new Error("必須のファイルを選択してください。");
        }
        continue;
      }

      const file = input.files[0];
      if (!ALLOWED_MIME.includes(file.type)) {
        throw new Error("対応形式は JPG / PNG / PDF のみです。");
      }
      if (file.size > MAX_FILE_BYTES) {
        throw new Error("ファイルサイズは5MB以下にしてください。");
      }

      files.push({
        name: file.name,
        mimeType: file.type,
        label: input.getAttribute("data-file-label") || file.name,
        data: await readFileAsBase64(file)
      });
    }

    if (!files.length) {
      throw new Error("ファイルを1つ以上選択してください。");
    }

    return files;
  }

  function buildPayload(form, formType) {
    const fd = new FormData(form);
    return {
      uploadToken: config.uploadToken || "",
      uploadTokenIssuedAt: config.uploadTokenIssuedAt || "",
      formType,
      email: String(fd.get("email") || config.customerEmail || "").trim(),
      shopifyCustomerId: String(fd.get("shopifyCustomerId") || getCustomerId() || "").trim(),
      name: String(fd.get("name") || "").trim(),
      birthdate: String(fd.get("birthdate") || "").trim(),
      idType: String(fd.get("idType") || "").trim(),
      company: String(fd.get("company") || "").trim(),
      storeName: String(fd.get("storeName") || "").trim(),
      phone: String(fd.get("phone") || "").trim(),
      address: String(fd.get("address") || "").trim(),
      note: String(fd.get("note") || "").trim()
    };
  }

  function formatSubmitError(error, responseStatus) {
    const raw = String((error && error.message) || error || "");
    if (/failed to fetch|networkerror|load failed/i.test(raw)) {
      return "書類送信サーバーに接続できません。しばらくしてから再試行してください。";
    }
    if (responseStatus === 403) {
      return "書類送信サーバーへのアクセスが拒否されました。管理者にお問い合わせください。";
    }
    if (responseStatus >= 500) {
      return "書類送信サーバーでエラーが発生しました。しばらくしてから再試行してください。";
    }
    return raw || "送信に失敗しました。";
  }

  function shouldNotifySubmitError(formType) {
    try {
      const raw = sessionStorage.getItem(ERROR_NOTIFY_SESSION_KEY);
      if (!raw) return true;
      const data = JSON.parse(raw);
      if (!data || data.formType !== formType) return true;
      return Date.now() - data.at >= ERROR_NOTIFY_COOLDOWN_MS;
    } catch (_) {
      return true;
    }
  }

  function markSubmitErrorNotified(formType) {
    try {
      sessionStorage.setItem(
        ERROR_NOTIFY_SESSION_KEY,
        JSON.stringify({ formType, at: Date.now() })
      );
    } catch (_) {
      /* ignore */
    }
  }

  function notifySubmitError(formType, errorMessage, payload) {
    if (!config.notifyEnabled || !config.errorWebhookUrl) return;
    if (!shouldNotifySubmitError(formType)) return;

    markSubmitErrorNotified(formType);

    const formLabel = formType === "b2b" ? "卸売申請" : "本人確認（B2C）";
    const lines = [
      "【Shisha Monster】書類提出エラー",
      "種別: " + formLabel,
      "エラー: " + errorMessage,
      "氏名: " + (payload.name || "—"),
      "メール: " + (payload.email || "—"),
      "ストア: " + window.location.origin,
      "時刻: " + new Date().toISOString()
    ];

    fetch(config.errorWebhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: lines.join("\n") }),
      keepalive: true
    }).catch((err) => {
      console.warn("[document-upload] error webhook failed", err);
    });
  }

  async function submitToGas(form, formType) {
    if (!config.gasUrl) {
      throw new Error("GAS URL が設定されていません。テーマ設定を確認してください。");
    }
    if (!config.uploadToken || !config.customerId) {
      throw new Error("送信用の認証情報がありません。ログイン状態を確認し、ページを再読み込みしてください。");
    }

    const payload = buildPayload(form, formType);
    // トークンはこの顧客ID に紐づけて署名されているため、必ず同じ ID を送る
    payload.shopifyCustomerId = String(config.customerId);
    payload.files = await collectFiles(form);

    let response;
    try {
      response = await fetch(config.gasUrl, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload)
      });
    } catch (networkError) {
      const err = new Error(formatSubmitError(networkError));
      err.isNetworkError = true;
      throw err;
    }

    if (!response.ok) {
      const err = new Error(formatSubmitError(null, response.status));
      err.httpStatus = response.status;
      throw err;
    }

    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (_) {
      const err = new Error("サーバーからの応答を読み取れませんでした。");
      err.rawResponse = text.substring(0, 200);
      throw err;
    }

    if (!data.ok) {
      throw new Error(data.error || "アップロードに失敗しました。");
    }

    return data;
  }

  function showFormError(form, message) {
    const errorEl = $("[data-document-form-error]", form.closest(".DocModal") || form.parentElement);
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function validateFormBeforeSubmit(form) {
    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }
    return true;
  }

  function showSuccess(modalEl, formType, data) {
    const formView = $("[data-document-modal-form-view]", modalEl);
    const successView = $("[data-document-modal-success-view]", modalEl);
    const successMessage = $("[data-document-success-message]", modalEl);

    if (formView) formView.hidden = true;
    if (successView) successView.hidden = false;

    if (successMessage) {
      if (formType === "b2c") {
        if (data.tagApplied) {
          if (isCartPage()) {
            successMessage.textContent =
              "アップロードが完了しました。まもなくチェックアウトを有効にします。この画面のままお待ちください。";
          } else {
            successMessage.textContent =
              "アップロードが完了しました。カート画面でチェックアウトを有効化します。下のボタンを押してください。";
          }
        } else {
          console.error("[document-upload] GAS response:", data);
          let detail = data.tagWarning;
          if (!detail) {
            if (data.tagApplied === undefined) {
              detail =
                "GAS が古いバージョンです。Code.gs を貼り付けて Web App を「新バージョン」で再デプロイしてください。";
            } else {
              detail = "原因不明（Apps Script → 実行数 → 直近の doPost を確認）";
            }
          }
          console.error("[document-upload] tagApplied=false", detail);
          successMessage.textContent =
            "アップロードは完了しましたが、チェックアウトの反映に失敗しました。\n" +
            "原因: " +
            detail +
            (data.codeVersion ? "\nGAS: " + data.codeVersion : "") +
            "\nページを更新しても checkout が表示されない場合は、お問い合わせください。";
        }
      }
    }

    if (formType === "b2c" && data.tagApplied) {
      setPendingCheckoutUnlock(getCustomerId());
      $all("[data-document-modal-close]", modalEl).forEach((el) => {
        el.hidden = true;
      });

      let reloadButton = $("[data-document-reload-checkout]", modalEl);
      if (!reloadButton && successView) {
        reloadButton = document.createElement("button");
        reloadButton.type = "button";
        reloadButton.className = "button button--primary button--full";
        reloadButton.setAttribute("data-document-reload-checkout", "");
        reloadButton.textContent = "チェックアウトを表示する";
        reloadButton.addEventListener("click", reloadPageForCheckout);
        successView.appendChild(reloadButton);
      } else if (reloadButton) {
        reloadButton.hidden = false;
      }
    }
  }

  function scheduleReloadAfterB2cSubmit(data, customerId) {
    if (!data || !data.tagApplied) return;

    setPendingCheckoutUnlock(customerId || getCustomerId());
    pendingB2cReload = true;
    if (reloadTimer) window.clearTimeout(reloadTimer);
    reloadTimer = window.setTimeout(reloadPageForCheckout, RELOAD_DELAY_MS);
  }

  async function onFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const formType = form.getAttribute("data-document-form");
    if (!formType) return;

    event.preventDefault();

    if (!validateFormBeforeSubmit(form)) {
      return;
    }

    const modalEl = form.closest(".DocModal");
    const submitButton = $(".DocModal__submit", form);
    const errorEl = $("[data-document-form-error]", modalEl);
    const payloadPreview = buildPayload(form, formType);

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = "";
    }

    if (submitButton) submitButton.disabled = true;
    isSubmitting = true;

    try {
      const data = await submitToGas(form, formType);
      showSuccess(modalEl, formType, data);

      if (formType === "b2c") {
        scheduleReloadAfterB2cSubmit(data, payloadPreview.shopifyCustomerId);
      }
    } catch (error) {
      const message = error.message || "送信に失敗しました。";
      showFormError(form, message);
      notifySubmitError(formType, message, payloadPreview);
    } finally {
      isSubmitting = false;
      if (!pendingB2cReload && submitButton) submitButton.disabled = false;
    }
  }

  function onDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openId = target.closest("[data-action='open-id-verification-modal']");
    if (openId) {
      event.preventDefault();
      openModal(document.getElementById("IdVerificationModal"));
      return;
    }

    const openB2b = target.closest("[data-action='open-wholesale-modal']");
    if (openB2b) {
      event.preventDefault();
      event.stopPropagation();
      openModal(document.getElementById("WholesaleRegisterModal"));
      return;
    }

    const wholesaleHashLink = target.closest("a[href='#open-wholesale-modal'], a[href='#wholesale-register']");
    if (wholesaleHashLink) {
      event.preventDefault();
      event.stopPropagation();
      openModal(document.getElementById("WholesaleRegisterModal"));
      return;
    }

    if (target.closest("[data-document-modal-close]")) {
      event.preventDefault();
      const modalEl = target.closest(".DocModal");
      closeModal(modalEl);
    }
  }

  function onDocumentChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches("[data-id-type-select]")) return;
    const modalEl = target.closest(".DocModal");
    if (modalEl) updateIdBackField(modalEl);
  }

  function init() {
    window.addEventListener("beforeunload", warnBeforeUnload);
    document.addEventListener("click", onDocumentClick, true);
    document.addEventListener("change", onDocumentChange);
    document.addEventListener("submit", onFormSubmit);

    const boot = () => {
      initCheckoutGateBridge();
      maybeStartGateSync();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  }

  init();
})();
